import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Tag,
  Table,
  Progress,
  Select,
  Space,
  Spin,
} from 'antd';
import {
  DollarOutlined,
  CheckSquareOutlined,
  UserOutlined,
  StarOutlined,
  BellOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import { useOutletContext } from 'react-router-dom';

const { Text } = Typography;
const { Option } = Select;
type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

interface DashboardData {
  billing: {
    quotations: {
      total: number;
      pending: number;
      approved: number;
    };
    invoices: {
      total: number;
      unpaid: number;
      paid: number;
    };
  };
  tasks: {
    ongoing_total: number;
    by_status: {
      plan: number;
      progress: number;
      review: number;
      approve: number;
    };
  };
  people: {
    period: string;
    stats: {
      personal: number;
      sole_prop: number;
      sdn_bhd: number;
      partnership: number;
      llp: number;
    };
  };
  efficiency: {
    staff: {
      overall_efficiency: number;
      planned: number;
      actual: number;
      details: Array<{
        no: number;
        name: string;
        efficiency: string;
        planned: number;
        actual: number;
      }>;
    };
    team: {
      overall_efficiency: number;
      planned: number;
      actual: number;
      details: Array<{
        no: number;
        name: string;
        efficiency: string;
        planned: number;
        actual: number;
      }>;
    };
  };
  reviews: {
    total: number;
    average_rating: number;
    distribution: {
      5: number;
      4: number;
      3: number;
      2: number;
      1: number;
    };
  };
  due: {
    weeks: number;
    tasks: Array<{
      id: number;
      customer: string;
      task: string;
      year: number;
      due: string;
    }>;
  };
}

export const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData | null>(null);
  const [period, setPeriod] = useState('30');
  const [dueWeeks, setDueWeeks] = useState(1);
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();

  useEffect(() => {
    loadDashboardData();
  }, [period, dueWeeks]);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Dashboard
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/dashboard', {
        params: {
          period,
          due_weeks: dueWeeks,
        },
      });
      setData(response.data.data);
    } catch (error: any) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px' }}>
        <Text>No data available</Text>
      </div>
    );
  }

  // Efficiency table columns
  const efficiencyColumns = [
    {
      title: 'Ranking',
      dataIndex: 'no',
      key: 'no',
      width: 80,
    },
    {
      title: 'Staff',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Team',
      dataIndex: 'team',
      key: 'team',
    },
    {
      title: 'Efficiency',
      dataIndex: 'efficiency',
      key: 'efficiency',
      render: (text: string) => {
        const percentage = parseInt(text);
        const color = percentage >= 100 ? '#52c41a' : percentage >= 80 ? '#faad14' : '#ff4d4f';
        return <Tag color={color}>{text}</Tag>;
      },
    },
  ];

  // Combine staff and team efficiency data
  const efficiencyTableData = data.efficiency.staff.details.map((staff, index) => ({
    key: index,
    no: staff.no,
    name: staff.name,
    team: data.efficiency.team.details[index]?.name || 'N/A',
    efficiency: staff.efficiency,
  }));

  // Due table columns
  const dueColumns = [
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
    },
    {
      title: 'Task',
      dataIndex: 'task',
      key: 'task',
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'Due',
      dataIndex: 'due',
      key: 'due',
    },
  ];

  // Customer New table data
  const customerNewColumns = [
    {
      title: 'New',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: '30 Days',
      dataIndex: 'count',
      key: 'count',
    },
  ];

  const customerNewData = [
    { key: '1', type: 'Personal', count: data.people.stats.personal },
    { key: '2', type: 'Sole Proprietorship', count: data.people.stats.sole_prop },
    { key: '3', type: 'Partnership', count: data.people.stats.partnership },
    { key: '4', type: 'Private Limited Company', count: data.people.stats.sdn_bhd },
    { key: '5', type: 'Limited Liability Partnership', count: data.people.stats.llp },
  ];

  // Customer Review table data
  const customerReviewColumns = [
    {
      title: 'Review',
      dataIndex: 'rating',
      key: 'rating',
    },
    {
      title: '30 Days',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (text: number) => `${text.toFixed(0)}%`,
    },
  ];

  const customerReviewData = [
    { key: '1', rating: '5 Stars', percentage: data.reviews.total > 0 ? (data.reviews.distribution[5] / data.reviews.total) * 100 : 0 },
    { key: '2', rating: '4 Stars', percentage: data.reviews.total > 0 ? (data.reviews.distribution[4] / data.reviews.total) * 100 : 0 },
    { key: '3', rating: '3 Stars', percentage: data.reviews.total > 0 ? (data.reviews.distribution[3] / data.reviews.total) * 100 : 0 },
    { key: '4', rating: '2 Stars', percentage: data.reviews.total > 0 ? (data.reviews.distribution[2] / data.reviews.total) * 100 : 0 },
    { key: '5', rating: '1 Star', percentage: data.reviews.total > 0 ? (data.reviews.distribution[1] / data.reviews.total) * 100 : 0 },
  ];

  // Tasks Ongoing table data
  const tasksOngoingColumns = [
    {
      title: 'Ongoing',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: '',
      dataIndex: 'count',
      key: 'count',
    },
  ];

  const tasksOngoingData = [
    { key: '1', status: 'Plan', count: data.tasks.by_status.plan },
    { key: '2', status: 'Progress', count: data.tasks.by_status.progress },
    { key: '3', status: 'Review', count: data.tasks.by_status.review },
    { key: '4', status: 'Approved', count: data.tasks.by_status.approve },
  ];

  // Billing Outstanding table data
  const billingOutstandingColumns = [
    {
      title: 'Outstanding',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: '',
      dataIndex: 'count',
      key: 'count',
    },
  ];

  const billingOutstandingData = [
    { key: '1', type: 'Quotation', count: data.billing.quotations.pending },
    { key: '2', type: 'Invoice', count: data.billing.invoices.unpaid },
  ];

  return (
    <div>
      {/* First Row: Customer New, Customer Review, Tasks Ongoing, Billing Outstanding */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Customer New */}
        <Col xs={24} sm={12} md={6}>
          <Card
            title="New Customer"
            style={{ height: '100%' }}
            bodyStyle={{ padding: 0 }}
          >
            <Table
              columns={customerNewColumns}
              dataSource={customerNewData}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        {/* Customer Review */}
        <Col xs={24} sm={12} md={6}>
          <Card
            title="Customer Review"
            style={{ height: '100%' }}
            bodyStyle={{ padding: 0 }}
          >
            <Table
              columns={customerReviewColumns}
              dataSource={customerReviewData}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        {/* Tasks Ongoing */}
        <Col xs={24} sm={12} md={6}>
          <Card
            title="Tasks Ongoing"
            style={{ height: '100%' }}
            bodyStyle={{ padding: 0 }}
          >
            <Table
              columns={tasksOngoingColumns}
              dataSource={tasksOngoingData}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        {/* Billing Outstanding */}
        <Col xs={24} sm={12} md={6}>
          <Card
            title="Billing Outstanding"
            style={{ height: '100%' }}
            bodyStyle={{ padding: 0 }}
          >
            <Table
              columns={billingOutstandingColumns}
              dataSource={billingOutstandingData}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      {/* Second Row: Tasks Efficiency and Tasks Due */}
      <Row gutter={[16, 16]}>
        {/* Tasks Efficiency Section */}
        <Col xs={24} lg={12}>
          <Card
            title="Tasks Efficiency"
            style={{ height: '100%' }}
          >
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={data.efficiency.staff.overall_efficiency}
                      format={(percent) => `${percent}%`}
                      strokeColor="#fa8c16"
                      size={120}
                    />
                    <div style={{ marginTop: 8, fontSize: 16, fontWeight: 600 }}>Staff</div>
                    <div style={{ marginTop: 8, fontSize: 20, fontWeight: 600, color: '#fa8c16' }}>
                      {data.efficiency.staff.overall_efficiency}% Excellent
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ border: '2px solid #fa8c16', padding: '8px', borderRadius: 4, textAlign: 'center' }}>
                      <Text style={{ fontSize: 12 }}>Planned</Text>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>{data.efficiency.staff.planned}</div>
                    </div>
                    <div style={{ border: '2px solid #fa8c16', padding: '8px', borderRadius: 4, textAlign: 'center' }}>
                      <Text style={{ fontSize: 12 }}>Actual</Text>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>{data.efficiency.staff.actual}</div>
                    </div>
                  </div>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ textAlign: 'center' }}>
                    <Progress
                      type="circle"
                      percent={data.efficiency.team.overall_efficiency}
                      format={(percent) => `${percent}%`}
                      strokeColor="#722ed1"
                      size={120}
                    />
                    <div style={{ marginTop: 8, fontSize: 16, fontWeight: 600 }}>Team</div>
                    <div style={{ marginTop: 8, fontSize: 20, fontWeight: 600, color: '#722ed1' }}>
                      {data.efficiency.team.overall_efficiency}% Excellent
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ border: '2px solid #722ed1', padding: '8px', borderRadius: 4, textAlign: 'center' }}>
                      <Text style={{ fontSize: 12 }}>Planned</Text>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>{data.efficiency.team.planned}</div>
                    </div>
                    <div style={{ border: '2px solid #722ed1', padding: '8px', borderRadius: 4, textAlign: 'center' }}>
                      <Text style={{ fontSize: 12 }}>Actual</Text>
                      <div style={{ fontSize: 16, fontWeight: 600 }}>{data.efficiency.team.actual}</div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
            <Table
              columns={efficiencyColumns}
              dataSource={efficiencyTableData}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>

        {/* Tasks Due Section */}
        <Col xs={24} lg={12}>
          <Card
            title="Tasks Due"
            style={{ height: '100%' }}
          >
            <Table
              columns={dueColumns}
              dataSource={data.due.tasks}
              pagination={false}
              size="small"
              rowKey="id"
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};
